import React, { useMemo, useState } from 'react';

import { PlusOutlined, EditOutlined, AppstoreOutlined } from '@ant-design/icons';
import { Card, Table, Button, Space, Modal, Form, Input, message, Empty, Typography, Alert } from 'antd';
import { useTranslation } from 'react-i18next';

import { useOperatorData } from '@contexts/operator/OperatorDataContext';
import ListPageHeader from '@shared/ListPageHeader';

import { operatorApiFetch } from '@services/operator/apiService';

const { Title, Paragraph, Text } = Typography;

function OperatorInventoryTypesList() {
  const { t } = useTranslation();
  const { inventoryTypes, loading, refreshOperatorData } = useOperatorData();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);

  // 打開新增 Modal
  const handleAdd = () => {
    setEditingRecord(null);
    form.resetFields();
    setIsModalVisible(true);
  };

  // 打開編輯 Modal
  const handleEdit = (record) => {
    setEditingRecord(record);
    form.setFieldsValue({
      code: record.code,
      name: record.name,
    });
    setIsModalVisible(true);
  };

  // 關閉 Modal
  const handleCancel = () => {
    setIsModalVisible(false);
    setEditingRecord(null);
    form.resetFields();
  };

  // 提交表單
  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);

      let response;
      if (editingRecord) {
        // 更新
        response = await operatorApiFetch(`inventory_types/${editingRecord.id}`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ inventory_type: values }),
        });
      } else {
        // 新增
        response = await operatorApiFetch('inventory_types', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ inventory_type: values }),
        });
      }

      const data = await response.json();

      if (data.success) {
        message.success(
          editingRecord
            ? t('common.message.update_success')
            : t('common.message.create_success')
        );
        handleCancel();
        // 重新載入資料
        refreshOperatorData();
      } else {
        // 顯示錯誤訊息
        if (data.errors) {
          try {
            const errorMessages = Object.entries(data.errors)
              .map(([field, details]) => {
                const msgs = details.map(d =>
                  typeof d === 'object' ? t(`common.errors.messages.${d.message}`, d.interpolations || {}) : d
                );
                return `${t(`models.inventory_type.${field}`)}: ${msgs.join(', ')}`;
              })
              .join('\n');
            message.error(errorMessages);
          } catch (parseError) {
            console.error('Error parsing validation errors:', parseError, data.errors);
            message.error(t('common.error.validation_failed'));
          }
        } else {
          message.error(data.message || t('common.error.operation_failed'));
        }
      }
    } catch (error) {
      if (error.errorFields) {
        // 表單驗證錯誤
        message.error(t('common.message.please_check_form'));
      } else {
        console.error('Save error:', error);
        message.error(t('common.error.operation_failed'));
      }
    } finally {
      setSaving(false);
    }
  };

  const columns = useMemo(() => [
    {
      title: t('models.inventory_type.code'),
      dataIndex: 'code',
      key: 'code',
      width: 150,
    },
    {
      title: t('models.inventory_type.name'),
      dataIndex: 'name',
      key: 'name',
      width: 200,
    },
    {
      title: t('common.actions'),
      key: 'actions',
      width: 120,
      fixed: 'right',
      render: (_, record) => (
        <Space size="small">
          <Button
            type="link"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            {t('common.button.edit')}
          </Button>
        </Space>
      ),
    },
  ], [t]);

  // 檢測是否為空列表（首次使用）
  const isEmpty = !loading && (!inventoryTypes || inventoryTypes.length === 0);

  // 空狀態的歡迎畫面
  const renderEmptyState = () => (
    <div style={{ padding: '40px 20px', maxWidth: 800, margin: '0 auto' }}>
      <Empty
        image={<AppstoreOutlined style={{ fontSize: 64, color: '#1890ff' }} />}
        description={null}
      >
        <Typography>
          <Title level={4} style={{ marginBottom: 16 }}>
            {t('operator.pages.master_data.inventory_types.empty_state.title')}
          </Title>
          <Paragraph type="secondary" style={{ fontSize: 15, marginBottom: 24 }}>
            {t('operator.pages.master_data.inventory_types.empty_state.description')}
          </Paragraph>

          <Alert
            type="info"
            showIcon={false}
            style={{ textAlign: 'left', marginBottom: 24 }}
            message={
              <div>
                <Text strong style={{ display: 'block', marginBottom: 8 }}>
                  {t('operator.pages.master_data.inventory_types.empty_state.examples_title')}
                </Text>
                <ul style={{ margin: 0, paddingLeft: 20 }}>
                  <li><Text>{t('operator.pages.master_data.inventory_types.empty_state.example_good')}</Text></li>
                  <li><Text>{t('operator.pages.master_data.inventory_types.empty_state.example_defect')}</Text></li>
                  <li><Text>{t('operator.pages.master_data.inventory_types.empty_state.example_sample')}</Text></li>
                </ul>
              </div>
            }
          />

          <Paragraph type="secondary" style={{ fontSize: 13, marginBottom: 24 }}>
            {t('operator.pages.master_data.inventory_types.empty_state.hint')}
          </Paragraph>

          <Button
            type="primary"
            size="large"
            icon={<PlusOutlined />}
            onClick={handleAdd}
          >
            {t('operator.pages.master_data.inventory_types.empty_state.create_first')}
          </Button>
        </Typography>
      </Empty>
    </div>
  );

  return (
    <div className="page-container">
      <ListPageHeader
        items={['warehouse_settings', 'warehouse_settings.inventory_types']}
        actions={
          !isEmpty && (
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAdd}
            >
              {t('common.button.add')}
            </Button>
          )
        }
      />
      <Card bordered={false}>
        {isEmpty ? renderEmptyState() : (
          <Table
            columns={columns}
            dataSource={inventoryTypes}
            rowKey="id"
            loading={loading}
            pagination={{
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total) => t('common.table.total_items', { count: total }),
            }}
          />
        )}
      </Card>

      {/* 新增/編輯 Modal */}
      <Modal
        title={
          editingRecord
            ? `${t('common.button.edit')} ${t('models.inventory_type._')}`
            : `${t('common.button.add')} ${t('models.inventory_type._')}`
        }
        open={isModalVisible}
        onOk={handleSubmit}
        onCancel={handleCancel}
        confirmLoading={saving}
        okText={t('common.button.save')}
        cancelText={t('common.button.cancel')}
        width={500}
      >
        <Form
          form={form}
          layout="vertical"
          style={{ marginTop: 24 }}
        >
          <Form.Item
            name="code"
            label={t('models.inventory_type.code')}
            rules={[
              { required: true, message: t('common.validation.required') },
              { max: 10, message: t('common.validation.max_length', { count: 10 }) },
            ]}
          >
            <Input
              placeholder={t('common.placeholder.please_enter')}
              maxLength={10}
            />
          </Form.Item>

          <Form.Item
            name="name"
            label={t('models.inventory_type.name')}
            rules={[
              { required: true, message: t('common.validation.required') },
              { max: 50, message: t('common.validation.max_length', { count: 50 }) },
            ]}
          >
            <Input
              placeholder={t('common.placeholder.please_enter')}
              maxLength={50}
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}

export default OperatorInventoryTypesList;
